import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';   // NAV-1
import api, { fetchAllPages } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ShiftNotesCard } from '@/components/calendar/ShiftNotesCard';
import { PickupRequestsList, pickupRequestsOf } from '@/components/calendar/PickupRequestsList';   // client#9
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { UserPlus, Globe, Search, Car, AlertTriangle, Star, RefreshCw, Zap, BarChart3, X, Clock, MapPin, Users, Edit3, History, Loader2, Eye, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import SearchSelect from '@/components/common/SearchSelect';
import { STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';

function TravelIcon({ minutes, risk }) {
  if (!risk || risk === 'LOW') return null;
  const color = risk === 'HIGH' ? 'text-red-500' : 'text-amber-500';
  return (
    <TooltipProvider><Tooltip><TooltipTrigger>
      <Car className={`w-4 h-4 ${color}`} data-testid="travel-indicator" />
    </TooltipTrigger><TooltipContent><p>{minutes}min travel ({risk})</p></TooltipContent></Tooltip></TooltipProvider>
  );
}

function FatigueIcon({ risk }) {
  if (!risk || risk === 'NONE') return null;
  const color = risk === 'BLOCK' ? 'text-red-500' : 'text-amber-500';
  return (
    <TooltipProvider><Tooltip><TooltipTrigger>
      <AlertTriangle className={`w-4 h-4 ${color}`} data-testid="fatigue-indicator" />
    </TooltipTrigger><TooltipContent><p>Fatigue: {risk}</p></TooltipContent></Tooltip></TooltipProvider>
  );
}

function ContinuityIcon({ level }) {
  if (!level) return null;
  const color = level === 'GOOD' ? 'text-green-500' : level === 'MODERATE' ? 'text-amber-500' : 'text-red-500';
  return (
    <TooltipProvider><Tooltip><TooltipTrigger>
      <RefreshCw className={`w-4 h-4 ${color}`} data-testid="continuity-indicator" />
    </TooltipTrigger><TooltipContent><p>Continuity: {level}</p></TooltipContent></Tooltip></TooltipProvider>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DAY 17: EDIT COMPLETED SHIFT SECTION
// ═══════════════════════════════════════════════════════════════════════════════

function EditCompletedShiftSection({ shift, onRefresh }) {
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [versions, setVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [form, setForm] = useState({
    start_time: '',
    end_time: '',
    break_duration: '',
    notes: '',
    edit_reason: '',
  });

  useEffect(() => {
    if (shift && editOpen) {
      setForm({
        start_time: shift.start_time || '',
        end_time: shift.end_time || '',
        break_duration: shift.break_duration?.toString() || '0',
        notes: shift.notes || '',
        edit_reason: '',
      });
      setValidation(null);
    }
  }, [shift, editOpen]);

  const loadHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/corrections/shifts/${shift.id}/versions`);
      setVersions(res.data.data || []);
    } catch (e) {
      toast.error('Failed to load version history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      const res = await api.post(`/corrections/shifts/${shift.id}/validate`, {
        start_time: form.start_time || undefined,
        end_time: form.end_time || undefined,
        break_duration: form.break_duration ? parseInt(form.break_duration) : undefined,
      });
      setValidation(res.data.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (form.edit_reason.length < 10) {
      toast.error('Correction reason must be at least 10 characters');
      return;
    }
    
    setLoading(true);
    try {
      const payload = { edit_reason: form.edit_reason };
      
      // Only include changed fields
      if (form.start_time !== shift.start_time) payload.start_time = form.start_time;
      if (form.end_time !== shift.end_time) payload.end_time = form.end_time;
      if (parseInt(form.break_duration) !== (shift.break_duration || 0)) {
        payload.break_duration = parseInt(form.break_duration) || 0;
      }
      if (form.notes !== (shift.notes || '')) payload.notes = form.notes;
      
      const res = await api.post(`/corrections/shifts/${shift.id}/edit-completed`, payload);
      toast.success(res.data.message || 'Shift corrected successfully');
      setEditOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Correction failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="edit-completed-shift-section">
      <Separator />
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Post-Completion Edit</p>
      
      {/* Version badge if shift has corrections */}
      {shift.version && shift.version > 1 && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">v{shift.version}</Badge>
          {shift.is_correction && <Badge className="bg-purple-600 text-white text-xs">Correction</Badge>}
        </div>
      )}
      
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={loadHistory}
          data-testid="view-shift-history-btn"
        >
          <History className="w-4 h-4 mr-1" />
          History
        </Button>
        <Button 
          size="sm"
          onClick={() => setEditOpen(true)}
          data-testid="edit-completed-shift-btn"
        >
          <Edit3 className="w-4 h-4 mr-1" />
          Edit Shift
        </Button>
      </div>

      {/* Version History Sheet */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent className="w-[400px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Version History
            </SheetTitle>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            {historyLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : versions.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No version history</p>
            ) : (
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
                {versions.map((v, idx) => (
                  <div key={v.id} className="relative pl-10 pb-6" data-testid={`version-${v.version || 1}`}>
                    <div className={cn(
                      "absolute left-2.5 w-3 h-3 rounded-full border-2",
                      v.is_superseded ? "bg-gray-300 border-gray-400" : 
                      v.is_correction ? "bg-purple-500 border-purple-600" : 
                      "bg-green-500 border-green-600"
                    )} />
                    <Card className={cn(v.is_superseded && "opacity-60")}>
                      <CardContent className="p-3 space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="text-xs">v{v.version || 1}</Badge>
                          {idx === 0 && !v.is_superseded && <Badge className="bg-blue-600 text-white text-xs">Current</Badge>}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><p className="text-xs text-muted-foreground">Date</p><p className="font-mono">{v.date}</p></div>
                          <div><p className="text-xs text-muted-foreground">Time</p><p className="font-mono">{v.start_time} - {v.end_time}</p></div>
                        </div>
                        {v.correction_reason && (
                          <div className="pt-2 border-t">
                            <p className="text-xs text-muted-foreground">Reason</p>
                            <p className="bg-orange-50 rounded p-2 text-xs text-orange-800">{v.correction_reason}</p>
                          </div>
                        )}
                        {v.corrected_at && (
                          <p className="text-xs text-muted-foreground">
                            Corrected: {new Date(v.corrected_at).toLocaleString()}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-shift-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Edit Completed Shift
            </DialogTitle>
            <DialogDescription>
              Correct shift times for {shift.date}. This creates a new version.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current Values */}
            <Card className="bg-muted/50">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Current Values</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-3 gap-3 text-sm">
                <div><p className="text-xs text-muted-foreground">Start</p><p className="font-mono">{shift.start_time}</p></div>
                <div><p className="text-xs text-muted-foreground">End</p><p className="font-mono">{shift.end_time}</p></div>
                <div><p className="text-xs text-muted-foreground">Break</p><p className="font-mono">{shift.break_duration || 0}m</p></div>
              </CardContent>
            </Card>

            {/* New Values */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">New Start</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="edit-start-time" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">New End</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="edit-end-time" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Break (mins)</Label>
                <Input type="number" min="0" value={form.break_duration} onChange={(e) => setForm({ ...form, break_duration: e.target.value })} data-testid="edit-break" />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Correction Reason (min 10 chars)</Label>
              <Textarea
                value={form.edit_reason}
                onChange={(e) => setForm({ ...form, edit_reason: e.target.value })}
                rows={2}
                placeholder="Explain why this correction is needed..."
                className={cn(form.edit_reason.length > 0 && form.edit_reason.length < 10 && "border-red-500")}
                data-testid="edit-reason"
              />
              <p className="text-xs text-muted-foreground">{form.edit_reason.length}/10</p>
            </div>

            {/* Validation Results */}
            {validation && (
              <Card className={cn("border", validation.valid ? "border-green-500 bg-green-50" : "border-red-500 bg-red-50")}>
                <CardContent className="p-3 space-y-2">
                  {validation.issues?.length > 0 && (
                    <div><p className="text-xs font-medium text-red-700">Issues:</p>
                      <ul className="list-disc list-inside text-xs text-red-600">
                        {validation.issues.map((issue, i) => <li key={i}>{issue}</li>)}
                      </ul>
                    </div>
                  )}
                  {validation.warnings?.length > 0 && (
                    <div><p className="text-xs font-medium text-orange-700">Warnings:</p>
                      <ul className="list-disc list-inside text-xs text-orange-600">
                        {validation.warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </div>
                  )}
                  {validation.valid && validation.issues?.length === 0 && (
                    <p className="text-xs text-green-700 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Validation passed</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleValidate} disabled={validating}>
              {validating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              Validate
            </Button>
            <Button onClick={handleSubmit} disabled={loading || form.edit_reason.length < 10} data-testid="submit-correction">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Edit3 className="h-4 w-4 mr-2" />}
              Apply Correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShiftDrawer({ shift, open, onClose, onRefresh }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState('');
  const [continuity, setContinuity] = useState(null);
  const [fatigue, setFatigue] = useState(null);
  const [travel, setTravel] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (!open || !shift) return;
    setSuggestions([]);
    setContinuity(null);
    setFatigue(null);
    setTravel(null);
    setShowSuggestions(false);

    // Load continuity
    if (shift.participant_id) {
      api.get(`/roster/intelligence/participants/${shift.participant_id}/continuity?days=30`)
        .then(r => setContinuity(r.data.data)).catch(() => {});
    }
    // Load fatigue + travel for assigned staff
    if (shift.assigned_staff_id) {
      api.get(`/roster/intelligence/fatigue/staff-summary/${shift.assigned_staff_id}?date=${shift.date}`)
        .then(r => setFatigue(r.data.data)).catch(() => {});
      api.get(`/roster/intelligence/travel/staff-day/${shift.assigned_staff_id}?date=${shift.date}`)
        .then(r => setTravel(r.data.data)).catch(() => {});
    }
  }, [open, shift]);

  const loadSuggestions = () => {
    if (!shift) return;
    setShowSuggestions(true);
    setLoading(true);
    api.get(`/roster/intelligence/shifts/${shift.id}/suggest-staff?limit=10`)
      .then(r => setSuggestions(r.data.data?.suggestions || []))
      .catch(() => toast.error('Failed to load suggestions'))
      .finally(() => setLoading(false));
  };

  const handleAssign = async (staffId) => {
    setAssigning(staffId);
    try {
      const res = await api.post(`/roster/shifts/${shift.id}/assign`, { staff_id: staffId });
      if (res.data.error) { toast.error(res.data.error.message); return; }
      const warnings = res.data.warnings || [];
      toast.success(warnings.length > 0 ? `Assigned with ${warnings.length} warning(s)` : 'Staff assigned');
      onRefresh(); onClose();
    } catch (e) {
      const d = e.response?.data?.detail || e.response?.data?.error?.message || 'Failed';
      toast.error(typeof d === 'string' ? d : d.message || 'Assignment failed');
    } finally { setAssigning(''); }
  };

  const scoreColor = (s) => s >= 70 ? 'text-green-600 bg-green-50' : s >= 40 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';
  const riskBadge = (r) => r === 'BLOCK' ? 'destructive' : r === 'WARNING' ? 'outline' : 'secondary';

  if (!shift) return null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[460px] sm:w-[520px] overflow-y-auto" data-testid="shift-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between pr-4">
            <span>Shift Details</span>
            <StatusBadge status={shift.status} />
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          {/* Shift Info */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-muted-foreground" /><span className="font-mono">{shift.date} {shift.start_time}-{shift.end_time}</span></div>
            <div className="flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" /><span>{shift.participant_name || 'N/A'}</span></div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /><span>{shift.service_name || 'N/A'}</span></div>
            {shift.staff_name && <div className="flex items-center gap-2"><UserPlus className="w-4 h-4 text-muted-foreground" /><span className="font-medium">{shift.staff_name}</span></div>}
          </div>

          {/* client#21: shift notes in the roster drawer */}
          <ShiftNotesCard shift={shift} onSaved={onRefresh} />

          <Separator />

          {/* Intelligence Indicators */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Intelligence</p>

            {/* Continuity */}
            {continuity && (
              <div className="flex items-center justify-between" data-testid="drawer-continuity">
                <div className="flex items-center gap-2"><RefreshCw className={`w-4 h-4 ${continuity.continuityLevel === 'GOOD' ? 'text-green-500' : continuity.continuityLevel === 'MODERATE' ? 'text-amber-500' : 'text-red-500'}`} /><span className="text-sm">Continuity</span></div>
                <div className="text-right">
                  <Badge variant={continuity.continuityLevel === 'GOOD' ? 'default' : 'outline'}>{continuity.continuityLevel}</Badge>
                  <p className="text-xs text-muted-foreground mt-0.5">{continuity.continuityScore}% · {continuity.uniqueStaffCount} staff · {continuity.totalShifts} shifts</p>
                  {continuity.primaryStaff && <p className="text-xs text-muted-foreground">Primary: {continuity.primaryStaff.staffName}</p>}
                </div>
              </div>
            )}

            {/* Fatigue */}
            {fatigue && (
              <div className="flex items-center justify-between" data-testid="drawer-fatigue">
                <div className="flex items-center gap-2"><AlertTriangle className={`w-4 h-4 ${fatigue.fatigueRisk === 'BLOCK' ? 'text-red-500' : fatigue.fatigueRisk === 'WARNING' ? 'text-amber-500' : 'text-green-500'}`} /><span className="text-sm">Fatigue</span></div>
                <div className="text-right">
                  <Badge variant={riskBadge(fatigue.fatigueRisk)}>{fatigue.fatigueRisk}</Badge>
                  <p className="text-xs text-muted-foreground mt-0.5">{fatigue.hoursToday}h today · {fatigue.hoursThisWeek}h week · {fatigue.consecutiveDays}d streak</p>
                </div>
              </div>
            )}

            {/* Travel */}
            {travel && travel.travelSegments?.length > 0 && (
              <div className="flex items-center justify-between" data-testid="drawer-travel">
                <div className="flex items-center gap-2"><Car className={`w-4 h-4 ${travel.maxTravelRisk === 'HIGH' ? 'text-red-500' : travel.maxTravelRisk === 'MEDIUM' ? 'text-amber-500' : 'text-green-500'}`} /><span className="text-sm">Travel</span></div>
                <div className="text-right">
                  <Badge variant={riskBadge(travel.maxTravelRisk === 'HIGH' ? 'WARNING' : 'NONE')}>{travel.totalTravelMinutes}min</Badge>
                  <p className="text-xs text-muted-foreground mt-0.5">{travel.shifts?.length} shifts · {travel.travelSegments.length} segments</p>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Suggest Staff Button & Panel */}
          {shift.status === 'OPEN' && !shift.assigned_staff_id && (
            <div className="space-y-3">
              {!showSuggestions ? (
                <Button className="w-full" onClick={loadSuggestions} data-testid="drawer-suggest-staff-btn">
                  <Star className="w-4 h-4 mr-2" />Suggest Staff
                </Button>
              ) : (
                <div className="space-y-2" data-testid="drawer-suggestions-panel">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Staff Suggestions</p>
                  {loading ? <p className="text-sm text-muted-foreground py-3 text-center">Analyzing...</p> : (
                    <>
                      {suggestions.map((s, i) => (
                        <Card key={s.staffId} className={i === 0 ? 'border-green-200' : ''}>
                          <CardContent className="py-2.5 px-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className={`text-base font-bold rounded-md px-2 py-0.5 ${scoreColor(s.suitabilityScore)}`}>{s.suitabilityScore}</div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">{s.staffName}</p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[11px] text-muted-foreground flex items-center gap-0.5"><Car className="w-3 h-3" />{s.travelMinutes}m</span>
                                    {s.fatigueRisk !== 'NONE' && <Badge variant={riskBadge(s.fatigueRisk)} className="text-[10px] px-1 py-0">{s.fatigueRisk}</Badge>}
                                    {i === 0 && <Badge className="bg-green-500 text-white text-[10px] px-1 py-0">Best</Badge>}
                                  </div>
                                </div>
                              </div>
                              <Button size="sm" disabled={assigning === s.staffId || s.fatigueRisk === 'BLOCK'} onClick={() => handleAssign(s.staffId)} data-testid={`drawer-assign-${s.staffId}`}>
                                {assigning === s.staffId ? '...' : 'Assign'}
                              </Button>
                            </div>
                            {s.warnings?.length > 0 && (
                              <div className="mt-1.5 pl-10">{s.warnings.map((w, j) => <p key={j} className="text-[11px] text-amber-600">{w}</p>)}</div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                      {suggestions.length === 0 && <p className="text-center py-3 text-sm text-muted-foreground">No suitable staff found</p>}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Day 17: Edit Completed Shift Button */}
          {(shift.status === 'COMPLETED' || shift.status === 'completed') && (
            <EditCompletedShiftSection shift={shift} onRefresh={onRefresh} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SuggestStaffDialog({ shift, open, onClose, onAssigned }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState('');

  useEffect(() => {
    if (!open || !shift) return;
    setLoading(true);
    api.get(`/roster/intelligence/shifts/${shift.id}/suggest-staff?limit=10`)
      .then(res => setSuggestions(res.data.data?.suggestions || []))
      .catch(() => toast.error('Failed to load suggestions'))
      .finally(() => setLoading(false));
  }, [open, shift]);

  const handleAssign = async (staffId) => {
    setAssigning(staffId);
    try {
      const res = await api.post(`/roster/shifts/${shift.id}/assign`, { staff_id: staffId });
      if (res.data.error) {
        toast.error(res.data.error.message);
        return;
      }
      const warnings = res.data.warnings || [];
      if (warnings.length > 0) {
        toast.success(`Assigned with ${warnings.length} warning(s)`);
      } else {
        toast.success('Staff assigned');
      }
      onAssigned();
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data?.error?.message || 'Failed';
      toast.error(typeof detail === 'string' ? detail : detail.message || 'Assignment failed');
    } finally { setAssigning(''); }
  };

  const scoreColor = (s) => s >= 70 ? 'text-green-600' : s >= 40 ? 'text-amber-600' : 'text-red-600';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" />Smart Staff Suggestions</DialogTitle>
          <DialogDescription>{shift?.date} {shift?.start_time}-{shift?.end_time} | {shift?.participant_name}</DialogDescription>
        </DialogHeader>
        {loading ? <p className="text-muted-foreground py-4">Analyzing staff suitability...</p> : (
          <div className="space-y-2">
            {suggestions.map((s, i) => (
              <Card key={s.staffId} className={i === 0 ? 'border-green-200 bg-green-50/30' : ''}>
                <CardContent className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`text-lg font-bold ${scoreColor(s.suitabilityScore)} w-10 text-center`}>{s.suitabilityScore}</div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{s.staffName}</p>
                        {i === 0 && <Badge className="bg-green-500 text-white text-[10px]">Best Match</Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Car className="w-3 h-3" />{s.travelMinutes}min
                        </span>
                        {s.fatigueRisk !== 'NONE' && (
                          <Badge variant={s.fatigueRisk === 'BLOCK' ? 'destructive' : 'outline'} className="text-[10px]">
                            <AlertTriangle className="w-3 h-3 mr-1" />{s.fatigueRisk}
                          </Badge>
                        )}
                      </div>
                      {s.warnings?.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {s.warnings.map((w, j) => <p key={j} className="text-[11px] text-amber-600">{w}</p>)}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    disabled={assigning === s.staffId || s.fatigueRisk === 'BLOCK'}
                    variant={s.fatigueRisk === 'BLOCK' ? 'outline' : 'default'}
                    onClick={() => handleAssign(s.staffId)}
                    data-testid={`suggest-assign-${s.staffId}`}
                  >
                    {assigning === s.staffId ? '...' : s.fatigueRisk === 'BLOCK' ? 'Blocked' : 'Assign'}
                  </Button>
                </CardContent>
              </Card>
            ))}
            {suggestions.length === 0 && <p className="text-center py-4 text-muted-foreground">No suitable staff found</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InsightsPanel({ insights }) {
  if (!insights) return null;
  const s = insights.summary || {};
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="roster-insights-panel">
      <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{s.totalUpcomingShifts || 0}</p><p className="text-xs text-muted-foreground">Upcoming</p></CardContent></Card>
      <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-red-600">{s.unassignedShifts || 0}</p><p className="text-xs text-muted-foreground">Unassigned</p></CardContent></Card>
      <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-amber-600">{s.staffWithHighOvertime || 0}</p><p className="text-xs text-muted-foreground">High Overtime</p></CardContent></Card>
      <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-purple-600">{s.understaffedHouses || 0}</p><p className="text-xs text-muted-foreground">Houses at Risk</p></CardContent></Card>
      <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{insights.travelInefficiencyScore || 0}%</p><p className="text-xs text-muted-foreground">Travel Score</p></CardContent></Card>
    </div>
  );
}

export default function RosterAdmin() {
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();   // NAV-1: dashboard deep link ?status=OPEN
  const [statusFilter, setStatusFilter] = useState(() => (['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'PARTIALLY_FILLED'].includes(searchParams.get('status')) ? searchParams.get('status') : 'all'));
  const [branchFilter, setBranchFilter] = useState('all');
  const [branches, setBranches] = useState([]);
  const [assignDialog, setAssignDialog] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [publishConfirm, setPublishConfirm] = useState(null);
  const [pickupApproveConfirm, setPickupApproveConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [suggestDialog, setSuggestDialog] = useState(null);
  const [insights, setInsights] = useState(null);
  const [shiftMeta, setShiftMeta] = useState({});
  const [drawerShift, setDrawerShift] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (branchFilter !== 'all') params.set('branchId', branchFilter);
      if (statusFilter !== 'all') params.set('status_filter', statusFilter);
      const [shiftsRes, staffRes, brRes] = await Promise.all([
        api.get(`/roster/shifts?${params.toString()}`),
        fetchAllPages('/admin/users').catch(() => []),   // NEW-007: all users, not the first page of 20
        api.get('/org/branches'),
      ]);
      const shiftData = shiftsRes.data.data || [];
      setShifts(shiftData);
      setStaff(staffRes || []);
      setBranches(brRes.data.data || []);

      // Load insights
      api.get('/roster/intelligence/insights').then(r => setInsights(r.data.data)).catch(() => {});

      // Load intelligence for assigned shifts (batch, non-blocking)
      const meta = {};
      for (const s of shiftData.slice(0, 20)) {
        if (s.assigned_staff_id && s.participant_id) {
          try {
            const [fatigueRes, contRes] = await Promise.all([
              api.get(`/roster/intelligence/fatigue/staff-summary/${s.assigned_staff_id}?date=${s.date}`).catch(() => null),
              api.get(`/roster/intelligence/participants/${s.participant_id}/continuity?days=30`).catch(() => null),
            ]);
            meta[s.id] = {
              fatigueRisk: fatigueRes?.data?.data?.fatigueRisk || 'NONE',
              continuityLevel: contRes?.data?.data?.continuityLevel || null,
            };
          } catch { /* ignore individual failures */ }
        }
      }
      setShiftMeta(meta);
    } catch (e) { toast.error('Failed to load shifts'); }
  }, [statusFilter, branchFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePublish = async () => {
    if (!publishConfirm) return;
    setActionLoading(true);
    try {
      await api.post(`/roster/shifts/${publishConfirm.id}/publish-open`);
      toast.success('Shift published as open'); setPublishConfirm(null); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const [approvingPickupId, setApprovingPickupId] = useState(null);
  const handlePickupApprove = async (staffId) => {
    if (!pickupApproveConfirm) return;
    setApprovingPickupId(staffId);
    try {
      await api.post(`/roster/shifts/${pickupApproveConfirm.id}/pickup-approve`, { staff_id: staffId });
      toast.success('Shift request approved'); setPickupApproveConfirm(null); fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === 'string' ? detail : detail?.message || detail?.error?.message) || 'Failed');
    }
    finally { setApprovingPickupId(null); }
  };

  const assignStaff = async () => {
    if (!assignDialog || !selectedStaff) return;
    try {
      const res = await api.post(`/roster/shifts/${assignDialog.id}/assign`, { staff_id: selectedStaff });
      if (res.data.error) {
        toast.error(res.data.error.message);
        return;
      }
      const warnings = res.data.warnings || [];
      if (warnings.length > 0) {
        toast.success(`Assigned with ${warnings.length} warning(s)`);
      } else {
        toast.success('Staff assigned');
      }
      setAssignDialog(null); setSelectedStaff(''); fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data?.error?.message;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to assign');
    }
  };

  const filtered = shifts.filter(s => {
    if (!search) return true;
    return s.participant_name?.toLowerCase().includes(search.toLowerCase()) ||
           s.staff_name?.toLowerCase().includes(search.toLowerCase()) ||
           s.date?.includes(search);
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="roster-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Roster / Shifts</h1>
          <p className="text-muted-foreground mt-1">Intelligent shift management with safety controls</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1"><Car className="w-3 h-3" />Travel</Badge>
          <Badge variant="outline" className="gap-1"><AlertTriangle className="w-3 h-3" />Fatigue</Badge>
          <Badge variant="outline" className="gap-1"><RefreshCw className="w-3 h-3" />Continuity</Badge>
        </div>
      </div>

      <InsightsPanel insights={insights} />

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 md:w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="roster-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="roster-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="OPEN">Open</SelectItem>
              <SelectItem value="ASSIGNED">Assigned</SelectItem>
              <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="INVOICED">Invoiced</SelectItem>
            </SelectContent>
          </Select>
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="w-[160px]" data-testid="roster-branch-filter"><SelectValue placeholder="Branch" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground">{filtered.length} shift(s)</p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Participant</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center w-20">Indicators</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No shifts found</TableCell></TableRow>
              ) : filtered.map(s => {
                const meta = shiftMeta[s.id] || {};
                return (
                  <TableRow key={s.id} className="hover:bg-muted/60 cursor-pointer" data-testid={`shift-row-${s.id}`} onClick={() => setDrawerShift(s)}>
                    <TableCell className="font-mono text-xs tabular-nums">{s.date}</TableCell>
                    <TableCell className="font-mono text-xs tabular-nums">{s.start_time}-{s.end_time}</TableCell>
                    <TableCell className="font-medium">{s.participant_name || 'N/A'}</TableCell>
                    <TableCell className="text-sm">{s.service_name || 'N/A'}</TableCell>
                    <TableCell>
                      {s.staff_name || (pickupRequestsOf(s).length
                        ? <span className="text-amber-600 text-xs" data-testid={`pickup-count-${s.id}`}>{pickupRequestsOf(s).length === 1 ? 'Shift requested' : `${pickupRequestsOf(s).length} shift requests`}</span>
                        : <span className="text-muted-foreground italic text-xs">Unassigned</span>)}
                    </TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <FatigueIcon risk={meta.fatigueRisk} />
                        <ContinuityIcon level={meta.continuityLevel} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        {s.status === 'OPEN' && !s.assigned_staff_id && !pickupRequestsOf(s).length && (
                          <>
                            <Button size="sm" variant="default" onClick={() => setSuggestDialog(s)} data-testid={`suggest-staff-${s.id}`}>
                              <Star className="h-3 w-3 mr-1" /> Smart
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setAssignDialog(s)} data-testid={`assign-shift-${s.id}`}>
                              <UserPlus className="h-3 w-3 mr-1" /> Assign
                            </Button>
                            {!s.is_open && (
                              <Button size="sm" variant="secondary" onClick={() => setPublishConfirm(s)} data-testid={`publish-shift-${s.id}`}>
                                <Globe className="h-3 w-3 mr-1" /> Publish
                              </Button>
                            )}
                          </>
                        )}
                        {pickupRequestsOf(s).length > 0 && s.status === 'OPEN' && (
                          <Button size="sm" onClick={() => setPickupApproveConfirm(s)} data-testid={`approve-pickup-${s.id}`}>
                            {pickupRequestsOf(s).length === 1 ? 'Approve' : `Review ${pickupRequestsOf(s).length} requests`}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Smart Suggest Dialog */}
      {/* Shift Drawer */}
      <ShiftDrawer
        shift={drawerShift}
        open={!!drawerShift}
        onClose={() => setDrawerShift(null)}
        onRefresh={fetchData}
      />

      <SuggestStaffDialog
        shift={suggestDialog}
        open={!!suggestDialog}
        onClose={() => setSuggestDialog(null)}
        onAssigned={() => { setSuggestDialog(null); fetchData(); }}
      />

      {/* Manual Assign Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Staff to Shift</DialogTitle><DialogDescription>Select a staff member for {assignDialog?.date} {assignDialog?.start_time}-{assignDialog?.end_time}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <SearchSelect
              options={staff.filter(s => s.role_name === 'Support Worker').map(s => ({ value: s.id, label: s.full_name || s.email || '', keywords: s.email || '' }))}
              value={selectedStaff}
              onChange={setSelectedStaff}
              placeholder="Select staff member"
              searchPlaceholder={STAFF_SEARCH_HINT}
              emptyText="No staff match that search"
              testId="assign-staff-select"
            />
            <Button className="w-full" onClick={assignStaff} disabled={!selectedStaff} data-testid="assign-confirm-button">Assign Staff</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!publishConfirm} onOpenChange={() => setPublishConfirm(null)} title="Publish Open Shift" description={publishConfirm ? `Publish the shift on ${publishConfirm.date} (${publishConfirm.start_time}-${publishConfirm.end_time}) as open for pickup?` : ''} confirmLabel="Publish Open" loading={actionLoading} onConfirm={handlePublish} />
      {/* client#9: every worker who requested the shift is listed; approve one */}
      <Dialog open={!!pickupApproveConfirm} onOpenChange={(o) => { if (!o) setPickupApproveConfirm(null); }}>
        <DialogContent className="max-w-md" data-testid="pickup-approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve Shift Request</DialogTitle>
            <DialogDescription>
              {pickupApproveConfirm ? `Shift on ${pickupApproveConfirm.date} ${pickupApproveConfirm.start_time || ''}-${pickupApproveConfirm.end_time || ''}. ` : ''}
              {pickupApproveConfirm && pickupRequestsOf(pickupApproveConfirm).length > 1 ? 'Choose who gets it — the others are told it went to someone else.' : ''}
            </DialogDescription>
          </DialogHeader>
          {pickupApproveConfirm && <PickupRequestsList shift={pickupApproveConfirm} onApprove={handlePickupApprove} approvingId={approvingPickupId} />}
          <DialogFooter><Button variant="outline" onClick={() => setPickupApproveConfirm(null)}>Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
